import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import {
  CancelIcon,
  CopyIcon,
  EditIcon,
  ProfileIcon,
  ViewMoreIcon,
} from 'components/icons';
import React, { ReactElement, useState } from 'react';
import { Event } from 'services/api';
import { Routes } from 'services/routes';
import { useRouter } from 'next/router';
import { Button } from 'components/ui';
import { EventHostStatus } from '../EventStatus';
import { Modal } from '../Modal';
import { EventBrief, EventBriefProps } from './EventBrief';

export interface HostedEventBriefProps extends EventBriefProps {
  status: Event['status'];
  canEdit: boolean;
  canCancel: boolean;
  canDuplicate?: boolean;
  onClickCancel: () => void;
}

export const HostedEventBrief = ({
  status,
  canEdit,
  canCancel,
  canDuplicate,
  onClickCancel,
  ...props
}: HostedEventBriefProps): ReactElement => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  const editEvent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          cursor: 'pointer',
        },
      }}
      onClick={() => {
        void router.push(Routes.editEvent(props.id));
      }}
    >
      <EditIcon />
      <Typography
        sx={{
          fontWeight: 500,
          ml: 5,
          fontSize: pxToEm(14),
        }}
      >
        Edit Event
      </Typography>
    </Box>
  );

  const editEventButton = (
    <Button
      variant="paper"
      style={{ marginRight: 16, marginBottom: 24, height: 60 }}
      onClick={() => {
        void router.push(Routes.editEvent(props.id));
      }}
    >
      <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
        <EditIcon />
      </Box>
      <Typography>Edit</Typography>
    </Button>
  );

  const duplicateEventButton = (
    <Button
      variant="paper"
      style={{ marginRight: 16, marginBottom: 24, height: 60 }}
      onClick={() => {
        Routes.createEvent('some-id');
      }}
    >
      <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
        <CopyIcon />
      </Box>
      <Typography>Duplicate</Typography>
    </Button>
  );

  const cancelEventButton = (
    <Button
      variant="paper"
      style={{ marginRight: 16, marginBottom: 24, height: 60 }}
      onClick={onClickCancel}
    >
      <Box mr={8} display="flex" alignItems={'center'} fontSize={pxToEm(30)}>
        <CancelIcon />
      </Box>
      <Typography>Cancel Event</Typography>
    </Button>
  );

  const viewMore = (
    <Box marginLeft="auto">
      <Box
        onClick={() => {
          setShowModal(true);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          borderRadius: '50%',
          backgroundColor: 'unset',
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: '#F5F5F5',
          },
        }}
      >
        <ViewMoreIcon />
      </Box>
    </Box>
  );

  const canViewMore = status !== 'REJECTED';

  const actionRow = (leftComponent: ReactElement) => (
    <Box display="flex" py={16}>
      {leftComponent}
      {canViewMore && viewMore}
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column" maxWidth={342}>
      <EventBrief {...props} />
      <EventHostStatus status={status} />

      {(status === 'PENDING_APPROVAL' || status === 'REJECTED') && (
        <Box mt={10}>{actionRow(editEvent)}</Box>
      )}

      <Modal
        title="Manage event"
        open={showModal}
        onClose={() => {
          setShowModal(false);
        }}
      >
        {canEdit && editEventButton}
        {canDuplicate && duplicateEventButton}
        {canCancel && cancelEventButton}
        <Typography
          onClick={() => {
            setShowModal(false);
          }}
          sx={{
            textAlign: 'center',
            '&:hover': {
              textDecoration: 'underline',
              cursor: 'pointer',
            },
          }}
        >
          Close
        </Typography>
      </Modal>
    </Box>
  );
};
